import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { updateMyCurrentWeight } from '../../api/profile';
import './style.css';

const QUESTIONS = [
  { key: 'weight', title: 'Current weight?', type: 'number', unit: 'lbs', required: true },

  {
    key: 'energy',
    title: 'How would you rate your energy this week?',
    type: 'rating',
    helper: '(1 = drained, low motivation / 5 = high energy, focused, and consistent)'
  },

  {
    key: 'mealConsistency',
    title: 'How consistently did you follow your meal plan?',
    type: 'choice',
    helper: 'Consistency is key for progress.',
    options: ['All', 'Most', 'Some', 'Rarely']
  },

  {
    key: 'digestion',
    title: 'How would you rate your digestion this week?',
    type: 'rating',
    helper: '(1 = extremely poor, bloating, inconsistent / 5 = smooth, regular, and feeling great)'
  },

  {
    key: 'training',
    title: 'How would you rate your training this week?',
    type: 'rating',
    helper: '(1 = struggled through sessions / 5 = felt strong, progressing, and performing well)'
  },

  {
    key: 'workoutConsistency',
    title: 'Did you complete your assigned workouts?',
    type: 'choice',
    helper: 'Better consistency = better progress.',
    options: ['All', 'Most', 'Some', 'None']
  },

  {
    key: 'recovery',
    title: 'How would you rate your recovery level?',
    type: 'rating',
    helper: '(1 = not recovering, always sore or fatigued / 5 = fully recovered, fresh, and ready to train)'
  },

  {
    key: 'painNotes',
    title: 'Any pain, injuries, or movements that felt off?',
    type: 'textarea',
    helper: 'Let us know about any issues so we can adjust your program as needed.'
  },

  {
    key: 'wellbeing',
    title: 'How was your overall mindset + wellbeing?',
    type: 'rating',
    helper: '(1 = overwhelmed, low mood / 5 = clear, focused, positive)'
  },

  {
    key: 'wins',
    title: 'Biggest win & biggest challenge this week?',
    type: 'textarea',
    helper: 'Let us know what went well and what could be improved for next week.'
  },
  {
    key: 'images',
    title: 'Progress photos (optional)',
    type: 'images',
    helper: 'Upload up to 5 images for your coach to review.'
  },
  {
    key: 'platformFeedback',
    title: 'Any feedback on the platform?',
    type: 'textarea',
    helper: 'Share bugs, ideas, or anything we can improve.'
  },
];


const initialState = {
  weight: '',
  energy: '',
  mealConsistency: '',
  digestion: '',
  training: '',
  workoutConsistency: '',
  recovery: '',
  painNotes: '',
  wellbeing: '',
  wins: '',
  images: [],
  platformFeedback: '',
};

const MAX_CHECKIN_IMAGES = 5;
const MAX_CHECKIN_IMAGE_MB = 5;
const MAX_CHECKIN_IMAGE_BYTES = MAX_CHECKIN_IMAGE_MB * 1024 * 1024;
const IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_IMAGE_BUCKET || 'coach-images';

const getFileExtension = (file) => {
  const name = file?.name || '';
  const nameExt = name.includes('.') ? name.split('.').pop() : '';
  const typeExt = file?.type?.split('/')?.[1] || '';
  const raw = nameExt || typeExt || 'jpg';
  const cleaned = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  return cleaned || 'jpg';
};

const buildImagePath = (userId, folder, file, index) => {
  const ext = getFileExtension(file);
  const unique = `${Date.now()}-${index}-${Math.random().toString(16).slice(2, 8)}`;
  return `${userId}/${folder}/${unique}.${ext}`;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = `${value}`.trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
};

export default function WeeklyCheckInFlow({ open, onClose, onCompleted, userId }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm(initialState);
      setError('');
      setImageError('');
    }
  }, [open]);

  const totalSteps = QUESTIONS.length;
  const progress = useMemo(
    () => Math.round(((step + 1) / totalSteps) * 100),
    [step, totalSteps]
  );
  const question = QUESTIONS[step];

  if (!open) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const removeImage = async (index) => {
    const target = form.images?.[index];
    if (target?.previewUrl && target.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(target.previewUrl);
    }
    if (target?.path) {
      try {
        await supabase.storage.from(IMAGE_BUCKET).remove([target.path]);
      } catch {
        // ignore delete issues
      }
    }
    setForm((prev) => {
      const next = [...(prev.images || [])];
      next.splice(index, 1);
      return { ...prev, images: next };
    });
  };

  const handleImagesChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setImageError('');

    if (!userId) {
      setImageError('Sign in to upload images.');
      return;
    }

    const current = form.images || [];
    const remaining = MAX_CHECKIN_IMAGES - current.length;
    if (remaining <= 0) {
      setImageError(`You can upload up to ${MAX_CHECKIN_IMAGES} images.`);
      return;
    }

    const next = [...current];
    const slice = files.slice(0, remaining);
    for (const [idx, file] of slice.entries()) {
      if (!file.type.startsWith('image/')) {
        setImageError('Only image files are allowed.');
        continue;
      }
      if (file.size > MAX_CHECKIN_IMAGE_BYTES) {
        setImageError(`Images must be ${MAX_CHECKIN_IMAGE_MB}MB or smaller.`);
        continue;
      }
      let previewUrl = '';
      try {
        const path = buildImagePath(userId, 'checkins', file, current.length + idx + 1);
        previewUrl = URL.createObjectURL(file);
        const { error: uploadError } = await supabase.storage
          .from(IMAGE_BUCKET)
          .upload(path, file, { upsert: false });
        if (uploadError) throw uploadError;
        next.push({ path, previewUrl, name: file.name });
      } catch {
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        setImageError('Unable to upload one of the images.');
      }
    }
    if (files.length > remaining) {
      setImageError(`You can upload up to ${MAX_CHECKIN_IMAGES} images.`);
    }
    update('images', next);
  };

  const validateStep = () => {
    const value = form[question.key];
    if (question.type === 'images') return true;
    if (question.type === 'textarea') return true;
    if (question.type === 'number') return toNumberOrNull(value) != null;
    return value !== '' && value !== null && value !== undefined;
  };

  const nextStep = () => {
    if (!validateStep()) {
      setError('Please answer before continuing.');
      return;
    }
    setStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const prevStep = () => {
    setError('');
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      setError('Please answer before continuing.');
      return;
    }
    if (!userId) {
      setError('Sign in to submit your check-in.');
      return;
    }
    setSubmitting(true);
    setError('');
    const imagePaths = (form.images || [])
      .map((img) => (typeof img === 'string' ? img : img?.path))
      .filter(Boolean);

    const feedbackMessage = form.platformFeedback?.trim() ? form.platformFeedback.trim() : null;
    const payload = {
      userid: userId,
      weight: toNumberOrNull(form.weight),
      energy_rating: toNumberOrNull(form.energy),
      meal_consistency: form.mealConsistency || null,
      digestion_rating: toNumberOrNull(form.digestion),
      training_rating: toNumberOrNull(form.training),
      workout_consistency: form.workoutConsistency || null,
      recovery_rating: toNumberOrNull(form.recovery),
      wellbeing_rating: toNumberOrNull(form.wellbeing),
      pain_notes: form.painNotes?.trim() ? form.painNotes.trim() : null,
      wins_summary: form.wins?.trim() ? form.wins.trim() : null,
      images: imagePaths,
    };
    try {
      if (
        payload.weight == null ||
        payload.energy_rating == null ||
        payload.digestion_rating == null ||
        payload.training_rating == null ||
        payload.recovery_rating == null ||
        payload.wellbeing_rating == null ||
        !payload.meal_consistency ||
        !payload.workout_consistency
      ) {
        throw new Error('Please complete all required questions.');
      }
      const { data, error: insertError } = await supabase
        .from('weekly_checkins')
        .insert([payload])
        .select('id, createdat')
        .single();
      if (insertError) throw insertError;
      if (feedbackMessage) {
        try {
          const { error: feedbackError } = await supabase
            .from('platform_feedback')
            .insert([{
              user_id: userId,
              checkin_id: data?.id ?? null,
              source: 'weekly_checkin',
              message: feedbackMessage,
            }]);
          if (feedbackError) throw feedbackError;
        } catch {
          // Best-effort feedback capture.
        }
      }
      try {
        await updateMyCurrentWeight(payload.weight, userId);
      } catch {
        // ignore weight sync failures
      }
      onCompleted?.(data?.createdat);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Unable to submit your check-in.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = () => {
    if (question.type === 'number') {
      return (
        <div className="weekly-flow-input-shell">
          <input
            type="number"
            className="weekly-flow-input"
            min="0"
            step="0.1"
            value={form[question.key]}
            placeholder="Enter value"
            onChange={(e) => update(question.key, e.target.value)}
          />
          {question.unit && <span className="weekly-flow-unit">{question.unit}</span>}
        </div>
      );
    }
    if (question.type === 'rating') {
      return (
        <div className="weekly-flow-options rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={`weekly-flow-choice ${form[question.key] === n ? 'selected' : ''}`}
              onClick={() => update(question.key, n)}
            >
              <span className="weekly-flow-choice-label">{n}</span>
            </button>
          ))}
        </div>
      );
    }
    if (question.type === 'choice') {
      return (
        <div className="weekly-flow-options choice">
          {question.options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`weekly-flow-choice ${form[question.key] === opt ? 'selected' : ''}`}
              onClick={() => update(question.key, opt)}
            >
              <span className="weekly-flow-choice-label">{opt}</span>
            </button>
          ))}
        </div>
      );
    }
    if (question.type === 'images') {
      return (
        <div className="weekly-flow-image-upload">
          <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
          <div className="weekly-flow-image-meta">
            <span>{form.images?.length || 0} of {MAX_CHECKIN_IMAGES} used</span>
          </div>
          {(form.images || []).length === 0 ? (
            <div className="weekly-flow-image-empty">No images added yet.</div>
          ) : (
            <div className="weekly-flow-image-grid">
              {(form.images || []).map((img, idx) => (
                <div key={`checkin-image-${idx}`} className="weekly-flow-image-card">
                  <img src={img.previewUrl || img.url || img.path} alt={`Weekly check-in upload ${idx + 1}`} loading="lazy" />
                  <button type="button" className="weekly-flow-image-remove" onClick={() => removeImage(idx)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {imageError && <div className="weekly-flow-image-error">{imageError}</div>}
        </div>
      );
    }
    return (
      <textarea
        rows="4"
        className="weekly-flow-textarea"
        value={form[question.key]}
        onChange={(e) => update(question.key, e.target.value)}
        placeholder="Share anything helpful..."
      />
    );
  };

  const isLastStep = step === totalSteps - 1;

  return (
    <div className="weekly-flow-overlay">
      <div className="weekly-flow-card">
        <div className="weekly-flow-top">
          <div className="weekly-flow-titlebar">
            <p className="weekly-flow-label">Weekly Check-In</p>
            <button className="weekly-flow-close" onClick={onClose} aria-label="Close check-in">
              &times;
            </button>
          </div>
          <div className="weekly-flow-progressrow">
            <div className="sections-progress">
              <div className="sections-track" aria-label="Progress">
                <div className="sections-fill" style={{ width: `${progress}%` }} />
                <div className="sections-dots">
                  {Array.from({ length: totalSteps }).map((_, idx) => (
                    <span
                      key={idx}
                      className={`step-dot ${idx === step ? 'active' : idx < step ? 'completed' : ''}`}
                    />
                  ))}
                </div>
              </div>
              <div className="sections-percent">{progress}%</div>
            </div>
          </div>
        </div>

        <div className="weekly-flow-head">
          <p className="weekly-flow-step">Step {step + 1} of {totalSteps}</p>
          <h2 className="weekly-flow-title">{question.title}</h2>
          {question.helper && <p className="weekly-flow-helper">{question.helper}</p>}
        </div>

        <div className="weekly-flow-body">
          {renderField()}
        </div>

        <div className="weekly-flow-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={prevStep}
            disabled={step === 0 || submitting}
          >
            Back
          </button>
          {isLastStep ? (
            <button
              type="button"
              className="primary-btn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit check-in'}
            </button>
          ) : (
            <button type="button" className="primary-btn" onClick={nextStep}>
              Next
            </button>
          )}
        </div>
        {error && <p className="form-feedback error weekly-flow-error">{error}</p>}
      </div>
    </div>
  );
}
